import ErrorOutlineIcon from "@mui/icons-material/ErrorOutlineOutlined";
import TabletAndroidOutlinedIcon from "@mui/icons-material/TabletAndroidOutlined";
import Alert from "@mui/material/Alert";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Snackbar from "@mui/material/Snackbar";
import Typography from "@mui/material/Typography";
import { isApiError, kioskStaffApi } from "@bw/shared/api";
import type { KioskCheckinItem } from "@bw/shared/types/generated";
import { useMemo, useState, type ReactNode } from "react";

import { createZonedTimeFormatter } from "@/libs/zonedDateTime";

import {
  KIOSK_REASON_LABEL,
  kioskInitials,
  kioskPlayerName,
  kioskPlayerTag,
  kioskScanDetail,
  kioskToolbarStatus,
  kioskUndoMessage,
} from "./kiosk-display";
import type { KioskFeedState } from "./use-kiosk-feed";

const MONO = '"Space Mono", ui-monospace, monospace';

function formatTime(value: string, formatter: Intl.DateTimeFormat): string {
  const instant = new Date(value);
  return Number.isNaN(instant.getTime()) ? "" : formatter.format(instant);
}

function PlayerPhoto({
  item,
  size = 24,
}: {
  item: KioskCheckinItem;
  size?: number;
}) {
  const name = kioskPlayerName(item);
  return (
    <Avatar
      src={item.avatar_url ?? undefined}
      alt=""
      sx={{
        width: size,
        height: size,
        fontSize: 10,
        fontWeight: 700,
        bgcolor: "action.selected",
        color: "text.secondary",
        flexShrink: 0,
      }}
    >
      {kioskInitials(name)}
    </Avatar>
  );
}

function PlayerName({ item }: { item: KioskCheckinItem }) {
  const tag = kioskPlayerTag(item);
  return (
    <Typography
      component="span"
      variant="body2"
      sx={{ fontWeight: 700, minWidth: 0, overflowWrap: "anywhere" }}
    >
      {kioskPlayerName(item)}
      {tag ? (
        <Box
          component="span"
          sx={{ fontWeight: 400, color: "text.disabled", ml: 0.5 }}
        >
          {tag}
        </Box>
      ) : null}
    </Typography>
  );
}

function RailCard({
  labelId,
  icon,
  title,
  meta,
  aside,
  tone,
  children,
}: {
  labelId: string;
  icon: ReactNode;
  title: string;
  meta?: ReactNode;
  aside?: ReactNode;
  tone?: "attention";
  children: ReactNode;
}) {
  return (
    <Paper
      component="section"
      aria-labelledby={labelId}
      variant="outlined"
      sx={{
        overflow: "hidden",
        ...(tone === "attention"
          ? { borderColor: "warning.light", borderWidth: 1.5 }
          : null),
      }}
    >
      <Box
        sx={{
          px: 1.5,
          py: 1,
          display: "flex",
          alignItems: "center",
          gap: 1,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Box sx={{ display: "flex", color: "text.secondary" }}>{icon}</Box>
        <Typography
          id={labelId}
          component="h2"
          variant="subtitle2"
          sx={{ fontWeight: 700 }}
        >
          {title}
        </Typography>
        {meta ? (
          <Typography variant="caption" color="text.secondary">
            {meta}
          </Typography>
        ) : null}
        {aside ? <Box sx={{ ml: "auto" }}>{aside}</Box> : null}
      </Box>
      {children}
    </Paper>
  );
}

const rowSx = {
  px: 1.5,
  py: 0.875,
  display: "flex",
  alignItems: "center",
  gap: 1,
  "&:not(:first-of-type)": { borderTop: 1, borderColor: "divider" },
} as const;

/**
 * The front desk's kiosk rail (#2691): reds that need a person, and every
 * kiosk scan live, green or red, with Undo. The kiosk never charges; Undo
 * checks the player out and gives back the visit only if the admission used one.
 */
export function KioskRail({
  facilityId,
  timeZone,
  feed,
}: {
  facilityId: string;
  timeZone: string;
  feed: KioskFeedState;
}) {
  const formatter = useMemo(
    () => createZonedTimeFormatter(timeZone),
    [timeZone],
  );
  const [busy, setBusy] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  if (feed.forbidden) return null;

  const run = async (
    scanId: string,
    action: () => Promise<string>,
  ): Promise<void> => {
    if (busy) return;
    setBusy(scanId);
    setActionError(null);
    try {
      setToast(await action());
    } catch (cause) {
      setActionError(
        isApiError(cause) && cause.message
          ? cause.message
          : "That didn't go through. Try again.",
      );
    } finally {
      setBusy(null);
    }
  };

  const resolve = (item: KioskCheckinItem) =>
    run(item.scan_id, async () => {
      await kioskStaffApi.resolveCheckin(facilityId, item.scan_id);
      feed.patch(item.scan_id, { resolved_at: new Date().toISOString() });
      return `${kioskPlayerName(item)} resolved.`;
    });

  const undo = (item: KioskCheckinItem) =>
    run(item.scan_id, async () => {
      const result = await kioskStaffApi.undoCheckin(facilityId, item.scan_id);
      feed.patch(item.scan_id, {
        undone_at: result.undone_at,
        can_undo: false,
      });
      return kioskUndoMessage(kioskPlayerName(item), result);
    });

  const status = kioskToolbarStatus(null, feed.devices);
  const needs = feed.needsAPerson;

  return (
    <Box sx={{ display: "grid", gap: 1.5, alignContent: "start", minWidth: 0 }}>
      {actionError ? (
        <Alert severity="error" onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      ) : null}

      <RailCard
        labelId="kiosk-needs-person-title"
        icon={<ErrorOutlineIcon fontSize="small" />}
        title="Needs a person"
        meta={feed.loading ? undefined : String(needs.length)}
        tone={needs.length > 0 ? "attention" : undefined}
      >
        {feed.loading ? (
          <Box
            sx={{ px: 1.5, py: 1 }}
            role="status"
            aria-label="Loading kiosk check-ins"
          >
            <Skeleton height={36} />
          </Box>
        ) : needs.length === 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ px: 1.5, py: 1.25 }}
          >
            Nobody is waiting on a person.
          </Typography>
        ) : (
          <Box component="ul" sx={{ m: 0, p: 0, listStyle: "none" }}>
            {needs.map((item) => (
              <Box
                component="li"
                key={item.scan_id}
                sx={rowSx}
                data-testid="needs-person-row"
              >
                <PlayerPhoto item={item} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <PlayerName item={item} />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: "block",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    <Box
                      component="span"
                      sx={{ fontWeight: 700, color: "text.primary" }}
                    >
                      {item.reason
                        ? KIOSK_REASON_LABEL[item.reason]
                        : "Needs a person"}
                    </Box>
                    {" · "}
                    {formatTime(item.scanned_at, formatter)}
                    {item.device_name ? `, ${item.device_name}` : ""}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  color="inherit"
                  onClick={() => void resolve(item)}
                  disabled={busy !== null}
                  aria-label={`Resolve ${kioskPlayerName(item)}`}
                  sx={{
                    minHeight: { xs: 44, md: 32 },
                    flexShrink: 0,
                    borderColor: "divider",
                  }}
                >
                  {busy === item.scan_id ? "Resolving…" : "Resolve"}
                </Button>
              </Box>
            ))}
          </Box>
        )}
      </RailCard>

      {/* Phone keeps only Needs a person; the live feed stays on wider screens. */}
      <Box sx={{ display: { xs: "none", md: "block" } }}>
        <RailCard
          labelId="kiosk-scans-title"
          icon={<TabletAndroidOutlinedIcon fontSize="small" />}
          title="Kiosk scans"
          meta="live"
          aside={
            feed.devices.some((d) => d.state === "active") ? (
              <Box
                component="span"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  px: 0.75,
                  py: 0.25,
                  borderRadius: 1,
                  typography: "caption",
                  fontWeight: 600,
                  color:
                    status.tone === "online"
                      ? "primary.main"
                      : "text.secondary",
                  bgcolor:
                    status.tone === "online"
                      ? "action.selected"
                      : "action.hover",
                }}
              >
                <Box
                  component="span"
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    bgcolor:
                      status.tone === "online"
                        ? "success.main"
                        : "text.disabled",
                  }}
                />
                {status.label.replace(/^Kiosk · /, "")}
              </Box>
            ) : undefined
          }
        >
          {feed.error ? (
            <Alert severity="warning" sx={{ m: 1 }}>
              {feed.error}
            </Alert>
          ) : null}
          {feed.loading ? (
            <Box sx={{ px: 1.5, py: 1 }}>
              <Skeleton height={36} />
              <Skeleton height={36} />
            </Box>
          ) : feed.items.length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ px: 1.5, py: 1.25 }}
            >
              No kiosk scans in the last 12 hours.
            </Typography>
          ) : (
            <Box
              component="ul"
              aria-live="polite"
              sx={{
                m: 0,
                p: 0,
                listStyle: "none",
                maxHeight: 420,
                overflowY: "auto",
              }}
            >
              {feed.items.map((item) => (
                <Box
                  component="li"
                  key={item.scan_id}
                  sx={rowSx}
                  data-testid="kiosk-scan-row"
                >
                  <Box
                    role="img"
                    aria-label={item.outcome === "green" ? "Green" : "Red"}
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      flexShrink: 0,
                      bgcolor: item.undone_at
                        ? "text.disabled"
                        : item.outcome === "green"
                          ? "success.main"
                          : "error.main",
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      fontFamily: MONO,
                      whiteSpace: "nowrap",
                      color: "text.secondary",
                      width: 58,
                    }}
                  >
                    {formatTime(item.scanned_at, formatter)}
                  </Typography>
                  <PlayerPhoto item={item} />
                  <Box
                    sx={{
                      minWidth: 0,
                      flex: 1,
                      opacity: item.undone_at ? 0.6 : 1,
                    }}
                  >
                    <PlayerName item={item} />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block" }}
                    >
                      {kioskScanDetail(item)}
                    </Typography>
                  </Box>
                  {item.can_undo ? (
                    <Button
                      size="small"
                      onClick={() => void undo(item)}
                      disabled={busy !== null}
                      aria-label={`Undo ${kioskPlayerName(item)}`}
                      sx={{
                        minHeight: { xs: 44, md: 32 },
                        minWidth: 0,
                        px: 1,
                        flexShrink: 0,
                      }}
                    >
                      {busy === item.scan_id ? "Undoing…" : "Undo"}
                    </Button>
                  ) : null}
                </Box>
              ))}
            </Box>
          )}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: "block",
              px: 1.5,
              py: 1,
              borderTop: 1,
              borderColor: "divider",
            }}
          >
            The kiosk never charges. Undo checks the player out and gives back
            the visit only if the check-in used one.
          </Typography>
        </RailCard>
      </Box>

      <Snackbar
        open={toast !== null}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        message={toast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Box>
  );
}
